import { acSetDisconnect } from "./ActionAuth"

const JSON_HEADER = { 'Accept': 'application/json', 'Content-Type': 'application/json' }
const FORM_DATA_HEADER = { 'Accept': 'application/json' }

/**
 * Builds a JSON header object for a fetch request.
 *
 * @author Maria Eduarda Franke
 * @param {string} m - The HTTP method (e.g. 'POST', 'PUT').
 * @param {object} b - The request body object.
 * @returns {object} - The JSON header object.
 */
const buildJsonHeader = (m, b) => ({
    method: m,
    headers: JSON_HEADER,
    body: JSON.stringify(b),
    credentials: 'include'
})

const buildFormDataHeader = (m, b) => ({
    method: m,
    headers: FORM_DATA_HEADER,
    body: b,
    credentials: 'include'
})

export const RESPONSE_TYPE = {
    JSON: 'json',
    TEXT: 'text',
    NONE: 'none',
    RESPONSE: 'response',
    BLOB: 'blob'
}

const handleFetchErrors = res => {
    return new Promise((resolve, reject) => {
        if (res.ok) { return resolve(res) }
        res.json()
            .then(j => {
                const headers = {}
                res.headers.forEach((value, name) => {
                    headers[name] = value
                })
                const err = { ...j, headers }
                reject(err)
            })
            .catch(_ => reject(res))
    })
}

/**
 * Executes a fetch call and handles the response flow.
 *
 * @author Maria Eduarda Franke
 * @param {Function} fetchCall - The fetch call to execute.
 * @param {Function} dispatch - The dispatch function from Redux.
 * @param {Function} onSuccess - The success action creator function.
 * @param {Function} onFailure - The failure action creator function.
 * @param {string} [respType=RESPONSE_TYPE.JSON] - The response type.
 * @returns {Promise} A promise that resolves with the result of the fetch call.
 *
 * @throws {Error} If the fetch call fails or the response status is not ok.
 */
const fetchFlow = (fetchCall, dispatch, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    return new Promise((resolve, reject) => {
        fetchCall()
            .then(handleFetchErrors)
            .then(res => {
                if (res.status === 204 && respType !== RESPONSE_TYPE.NONE) {
                    console.warn('Trying to get data from a 204 Response!')
                    return undefined
                }

                switch (respType) {
                    case RESPONSE_TYPE.JSON: return res.json()
                    case RESPONSE_TYPE.TEXT: return res.text()
                    case RESPONSE_TYPE.BLOB: return res.blob()
                    case RESPONSE_TYPE.NONE: return undefined
                    default: return res
                }
            })
            .then(result => {
                dispatch(onSuccess(result))
                resolve(result)
            })
            .catch(err => {
                // If the error status is 401 (and path equal authenticate), it will redirect the user to login, as the session is expired.
                if (err.status === 401) {

                    dispatch(acSetDisconnect())

                    if (err.path === 'authenticate') {
                        window.location.href = '/'
                    }
                }

                dispatch(onFailure(err))
                reject(err)
            })
    })
}

/**
 * Dispatches a fetch request and handles the flow of the request.
 *
 * @author Maria Eduarda Franke
 * @returns {Function} - A function that dispatches the onBegin action and returns a promise that resolves with the result of the fetch request.
 */
const dispatchFetch = (fetchCall, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    return dispatch => {
        dispatch(onBegin())
        return fetchFlow(fetchCall, dispatch, onSuccess, onFailure, respType)
    }
}

/**
 * Dispatches a GET request to the specified URL and handles the response using the provided callbacks.
 *
 * @author Maria Eduarda Franke
 * @param {string} url - The URL to send the GET request to.
 * @returns {function} - A function that dispatches the GET request and handles the response.
 */
export const dispatchFetchGet = (url, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    const fCall = () => fetch(url, { method: 'GET', credentials: 'include' })
    return dispatchFetch(fCall, onBegin, onSuccess, onFailure, respType)
}

/**
 * Dispatches a GET request to the specified URL and handles the file response using the provided callbacks.
 *
 * @author Maria Eduarda Franke
 * @param {string} url - The URL to send the GET request to.
 * @returns {function} - A function that dispatches the GET request and handles the file response.
 */
export const dispatchFetchGetFile = (url, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.BLOB) => {
    const fCall = () => fetch(url, { method: 'GET', credentials: 'include' })
    return dispatchFetch(fCall, onBegin, onSuccess, onFailure, respType)
}

/**
 * Dispatches a POST request to the specified URL with the given body.
 * 
 * @author Maria Eduarda Franke
 * @param {string} url - The URL to send the POST request to.
 * @param {object} body - The body of the request.
 * @returns {function} - A function that dispatches the request and returns a Promise.
 */
export const dispatchFetchPost = (url, body, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    const fCall = () => fetch(url, buildJsonHeader('POST', body || {}))
    return dispatchFetch(fCall, onBegin, onSuccess, onFailure, respType)
}

/**
 * @description Dispatches a POST request to the specified URL with the given form data body.
 * 
 * @author Maria Eduarda Franke
 * @param {string} url - The URL to send the request to.
 * @param {object} body - The form data body of the request.
 * @param {function} onBegin - The action to dispatch when the request begins.
 * @param {function} onSuccess - The action to dispatch when the request is successful.
 * @param {function} onFailure - The action to dispatch when the request fails.
 */
export const dispatchFetchPostFormData = (url, body, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    const fCall = () => fetch(url, buildFormDataHeader('POST', body))
    return dispatchFetch(fCall, onBegin, onSuccess, onFailure, respType)
}

/**
 * Dispatches a PUT request to the specified URL with the provided body.
 * 
 * @author Maria Eduarda Franke
 * @param {string} url - The URL to send the PUT request to.
 * @param {object} body - The body of the request.
 * @returns {function} - A function that dispatches the request and returns a Promise.
 */
export const dispatchFetchPut = (url, body, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    const fCall = () => fetch(url, buildJsonHeader('PUT', body || {}))
    return dispatchFetch(fCall, onBegin, onSuccess, onFailure, respType)
}

/**
 * Dispatches a DELETE request to the specified URL.
 *
 * @author Maria Eduarda Franke
 * @param {string} url - The URL to send the DELETE request to.
 * @returns {function} - A function that dispatches the DELETE request.
 */
export const dispatchFetchDelete = (url, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    const fCall = () => fetch(url, { method: 'DELETE', credentials: 'include' })
    return dispatchFetch(fCall, onBegin, onSuccess, onFailure, respType)
}

/**
 * Dispatches a custom fetch call with specified callbacks and response type.
 *
 * @author Maria Eduarda Franke
 * @param {Function} fetchCall - The fetch call to be executed.
 * @returns {Function} - The dispatch function.
 */
export const dispatchCustomFetch = (fetchCall, onBegin, onSuccess, onFailure, respType = RESPONSE_TYPE.JSON) => {
    return dispatchFetch(fetchCall, onBegin, onSuccess, onFailure, respType)
}

/**
 * Sets the search params in the specified URL.
 *
 * @author José Paludo, Maria Eduarda Franke
 * @param {string} originalUrl - The original URL.
 * @returns {URL} - The built URL with search params.
 */
export const setUrlSearchParams = (originalUrl, payload) => {

    const url = new URL(originalUrl, location.origin)

    if (payload.pagination) {
        url.searchParams.set('page', payload.pagination.pageIndex.toString())
        url.searchParams.set('size', payload.pagination.pageSize.toString())
    }

    if (payload.filter) {
        for (const { id, value } of payload.filter) {
            if (value) {
                url.searchParams.set(id, value.toString())
            }
        }
    }

    if (payload.sorting && payload.sorting[0]) {
        url.searchParams.set('sortBy', payload.sorting[0].id.toString())
        url.searchParams.set('desc', payload.sorting[0].desc.toString())
    }

    return url
}

/**
 * Triggers a file download in the browser from a Blob response.
 *
 * @author Maria Eduarda Franke
 * @param {Blob} blob - Binary content received from the API.
 * @param {string} [filename='download'] - Desired file name for the download.
 * @returns {void} Does not return anything.
 */
export const triggerBrowserDownload = (blob, filename = 'download') => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename + "_output.csv"
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
}
